import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Grid,
  chakra,
  useDisclosure,
} from "@chakra-ui/react"
import type { ReactNode } from "react"
import { useIntl } from "react-intl"

import { inverseColors } from "@/theme/values"

import { BarsIcon, MultiplyIcon } from "../Icons"
import MenuItems from "./MenuItems"
import { MenuLogo } from "./MenuLogo"

// Bare 44px touch target on the black bar: no border nor radius, the hover of the sidebar items;
// focus is the global :focus-visible ring (theme.ts).
const BarButton = chakra("button", {
  baseStyle: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSize: "44px",
    borderWidth: 0,
    borderRadius: 0,
    color: inverseColors.text,
    bg: "transparent",
    transition: "background-color 140ms",
    _hover: { bg: inverseColors.surfaceHover },
  },
})

// Bar content shared by the top bar and the panel, so the panel opens exactly over it: the button
// on the left, the seal centred, an empty cell balancing the button.
const BarRow = ({ button, onLogoClick }: { button: ReactNode; onLogoClick?: () => void }) => (
  <Grid
    flex="none"
    templateColumns="44px 1fr 44px"
    alignItems="center"
    justifyItems="center"
    h="56px"
    px="2"
  >
    {button}
    <MenuLogo size="10" onClick={onLogoClick} />
    <Box aria-hidden />
  </Grid>
)

// Mobile form of the sidebar (sidebar.css, off-screen panel below 1024px): a black top bar whose
// burger opens the menu entries in a black panel sliding in from the left.
export default function MobileMenuBar() {
  const intl = useIntl()
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <Box
      display={{ base: "block", lg: "none" }}
      flex="none"
      bg="surfaceInverse"
      color={inverseColors.text}
      data-testid="menu-mobile-bar"
    >
      <BarRow
        button={
          <BarButton
            type="button"
            aria-label={intl.formatMessage({ id: "menu.open" })}
            aria-expanded={isOpen}
            onClick={onOpen}
          >
            <BarsIcon boxSize="24px" aria-hidden />
          </BarButton>
        }
      />
      <Drawer isOpen={isOpen} onClose={onClose} placement="left">
        <DrawerOverlay />
        <DrawerContent
          aria-label={intl.formatMessage({ id: "menu.label" })}
          maxW="sidebarW"
          bg="surfaceInverse"
          color={inverseColors.text}
        >
          <Flex
            as="nav"
            aria-label={intl.formatMessage({ id: "menu.label" })}
            flexDir="column"
            h="full"
            minH={0}
          >
            <BarRow
              button={
                <BarButton
                  type="button"
                  aria-label={intl.formatMessage({ id: "menu.close" })}
                  onClick={onClose}
                >
                  <MultiplyIcon boxSize="24px" aria-hidden />
                </BarButton>
              }
              onLogoClick={onClose}
            />
            <MenuItems collapsed={false} onNavigate={onClose} />
          </Flex>
        </DrawerContent>
      </Drawer>
    </Box>
  )
}
